import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import VendorModal from './VendorModal';
import { orderedPartsApi, vendorsApi, equipmentApi } from '@/api';
import CompanySelect from '@/components/shared/CompanySelect';
import { toast } from '@/hooks/use-toast';
import type { OrderedPart, RepairRequest, Vendor } from '@/types';

const poSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  equipmentId: z.string().optional(),
  company: z.string().optional(),
  repairDate: z.string().optional(),
  description: z.string().optional(),
  vendorId: z.string().min(1, 'Vendor is required'),
  quotedPrice: z.coerce.number().min(0, 'Quoted price must be 0 or more'),
  actualPrice: z.coerce.number().min(0, 'Actual price must be 0 or more').optional(),
  paymentMethod: z.enum(['Credit Card', 'Business Account', 'Other']),
  cardLastFour: z.string().optional(),
  companyAccount: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.paymentMethod === 'Credit Card' && !data.cardLastFour?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['cardLastFour'],
      message: 'Last four digits required for credit card',
    });
  }
  if (data.paymentMethod === 'Business Account' && !data.companyAccount?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['companyAccount'],
      message: 'Company account is required',
    });
  }
});

type POFormData = z.infer<typeof poSchema>;

interface POFormProps {
  repairRequest?: RepairRequest;
  existingPart?: OrderedPart;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function POForm({ repairRequest, existingPart, onSuccess, onCancel }: POFormProps) {
  const queryClient = useQueryClient();
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const isStandalone = !repairRequest;
  const isEdit = !!existingPart;

  const today = new Date().toISOString().slice(0, 10);

  const form = useForm<POFormData>({
    resolver: zodResolver(poSchema),
    defaultValues: isEdit
      ? {
          title: existingPart.title,
          equipmentId: existingPart.equipmentId ? String(existingPart.equipmentId) : '',
          company: existingPart.company ?? '',
          repairDate: existingPart.repairDate ?? '',
          description: existingPart.description ?? '',
          vendorId: String(existingPart.vendorId),
          quotedPrice: (existingPart.quotedPrice ?? 0) / 100,
          actualPrice: existingPart.actualPrice != null ? existingPart.actualPrice / 100 : undefined,
          paymentMethod: existingPart.paymentMethod,
          cardLastFour: existingPart.cardLastFour ?? '',
          companyAccount: existingPart.companyAccount ?? '',
        }
      : {
          title: repairRequest?.problem?.slice(0, 80) ?? '',
          equipmentId: repairRequest?.equipmentId ? String(repairRequest.equipmentId) : '',
          company: repairRequest?.equipment?.company ?? '',
          repairDate: today,
          description: '',
          vendorId: '',
          quotedPrice: 0,
          actualPrice: undefined,
          paymentMethod: 'Business Account',
          cardLastFour: '',
          companyAccount: '',
        },
  });

  const paymentMethod = form.watch('paymentMethod');
  const selectedCompany = form.watch('company');

  const { data: vendors, refetch: refetchVendors } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => vendorsApi.list(),
  });

  const { data: equipmentData } = useQuery({
    queryKey: ['equipment', 'active', selectedCompany],
    queryFn: () =>
      equipmentApi.list({
        ...(selectedCompany ? { company: selectedCompany } : {}),
        pageSize: 500,
      }),
    enabled: isStandalone,
  });

  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => orderedPartsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderedParts'] });
      toast({ title: 'Part order created' });
      onSuccess();
    },
    onError: () => {
      toast({ title: 'Failed to create order', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      orderedPartsApi.update(String(existingPart!.id), payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderedParts'] });
      toast({ title: 'Order updated' });
      onSuccess();
    },
    onError: () => {
      toast({ title: 'Failed to update order', variant: 'destructive' });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const filteredEquipment = useMemo(() => {
    const items = equipmentData?.items ?? [];
    if (!equipmentSearch.trim()) return items;
    const q = equipmentSearch.toLowerCase();
    return items.filter((e) =>
      e.unitNumber.toLowerCase().includes(q) ||
      (e.description ?? '').toLowerCase().includes(q) ||
      (e.company ?? '').toLowerCase().includes(q)
    );
  }, [equipmentData, equipmentSearch]);

  function handleVendorCreated(vendor: Vendor) {
    refetchVendors();
    form.setValue('vendorId', String(vendor.id));
    setVendorModalOpen(false);
  }

  async function onSubmit(values: POFormData) {
    const payload = {
      title: values.title,
      description: values.description || undefined,
      repairDate: values.repairDate || undefined,
      quotedPrice: Math.round(values.quotedPrice * 100),
      actualPrice: values.actualPrice != null ? Math.round(values.actualPrice * 100) : undefined,
      vendorId: Number(values.vendorId),
      company: values.company || undefined,
      paymentMethod: values.paymentMethod,
      cardLastFour: values.cardLastFour || undefined,
      companyAccount: values.companyAccount || undefined,
    };

    if (isEdit) {
      await updateMutation.mutateAsync(payload);
    } else {
      await createMutation.mutateAsync({
        ...payload,
        ...(repairRequest?.id != null ? { requestId: repairRequest.id } : {}),
        ...(values.equipmentId ? { equipmentId: Number(values.equipmentId) } : {}),
      });
    }
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Part Ordered</FormLabel>
                <FormControl>
                  <Input placeholder="Part description / order name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="repairDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Repair Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rebill Company (if needed)</FormLabel>
                  <FormControl>
                    <CompanySelect
                      value={field.value ?? ''}
                      onValueChange={(val) => {
                        field.onChange(val);
                        // Reset equipment selection when company changes
                        if (isStandalone) form.setValue('equipmentId', '');
                      }}
                      placeholder="Select company…"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Equipment — searchable combobox for standalone 3rd party repairs */}
          {isStandalone && (
            <FormField
              control={form.control}
              name="equipmentId"
              render={({ field }) => {
                const selected = equipmentData?.items.find((e) => String(e.id) === field.value);
                return (
                  <FormItem>
                    <FormLabel>Equipment</FormLabel>
                    <div className="relative">
                      <button
                        type="button"
                        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                        onClick={() => setEquipmentDropdownOpen((o) => !o)}
                      >
                        <span className={selected ? 'text-foreground' : 'text-muted-foreground'}>
                          {selected
                            ? `${selected.unitNumber}${selected.description ? ` — ${selected.description}` : ''}${selected.company ? ` (${selected.company})` : ''}`
                            : 'Select equipment…'}
                        </span>
                        <svg className="h-4 w-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {equipmentDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
                          <div className="p-2">
                            <input
                              autoFocus
                              type="text"
                              placeholder="Search by unit, description, company…"
                              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                              value={equipmentSearch}
                              onChange={(e) => setEquipmentSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <ul className="max-h-56 overflow-y-auto pb-1">
                            {filteredEquipment.length === 0 && (
                              <li className="px-3 py-2 text-sm text-muted-foreground">No results.</li>
                            )}
                            {filteredEquipment.map((e) => (
                              <li
                                key={e.id}
                                className="cursor-pointer px-3 py-2 text-sm hover:bg-accent"
                                onMouseDown={() => {
                                  field.onChange(String(e.id));
                                  setEquipmentSearch('');
                                  setEquipmentDropdownOpen(false);
                                }}
                              >
                                <span className="font-medium">{e.unitNumber}</span>
                                {e.description && <span className="text-muted-foreground"> — {e.description}</span>}
                                {e.company && <span className="text-muted-foreground"> ({e.company})</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Additional details about the parts ordered…"
                    rows={3}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Vendor combobox */}
          <FormField
            control={form.control}
            name="vendorId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vendor</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor…" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {vendors?.map((v) => (
                      <SelectItem key={v.id} value={String(v.id)}>
                        {v.name}
                      </SelectItem>
                    ))}
                    <div className="border-t border-border mt-1 pt-1">
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 px-2 py-1.5 text-sm text-primary hover:bg-accent rounded-sm"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setVendorModalOpen(true);
                        }}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add new vendor…
                      </button>
                    </div>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="quotedPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quoted Price ($)</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} step="0.01" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="actualPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Actual Price ($) <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                  <FormControl>
                    <Input type="number" min={0} step="0.01" placeholder="—" {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.valueAsNumber)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="paymentMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Method</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Credit Card">Credit Card</SelectItem>
                      <SelectItem value="Business Account">Business Account</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Conditional payment fields */}
          {paymentMethod === 'Credit Card' && (
            <FormField
              control={form.control}
              name="cardLastFour"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Card Last Four Digits</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={4}
                      pattern="[0-9]{4}"
                      placeholder="1234"
                      className="w-28"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {paymentMethod === 'Business Account' && (
            <FormField
              control={form.control}
              name="companyAccount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Account</FormLabel>
                  <FormControl>
                    <Input placeholder="Account name or number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Order'}
            </Button>
          </div>
        </form>
      </Form>

      <VendorModal
        open={vendorModalOpen}
        onClose={() => setVendorModalOpen(false)}
        onCreated={handleVendorCreated}
      />
    </>
  );
}
